import type { DownloadedFile } from "@khazzin/api-client";
import { saveFile } from "@khazzin/api-client";
import type { Translate } from "@khazzin/i18n";
import { Download } from "lucide-react";
import { useState } from "react";
import { Button } from "../../primitives/Button";
import { DropdownMenu } from "../../primitives/Menu";

/**
 * Exporting what the screen is showing.
 *
 * **The current filters, not everything.** The list is already a question
 * somebody asked; an export that ignored it would answer a different one, and
 * on a catalogue of forty thousand rows the difference is the whole point of
 * having filtered.
 *
 * **Two formats behind one button.** CSV is what another system reads and XLSX
 * is what a person opens, and which one somebody wants is not knowable from
 * here — but it is one decision, not a second button in the toolbar.
 */

export interface ExportApi {
	downloadExport(
		type: string,
		filters?: URLSearchParams,
		format?: "csv" | "xlsx",
	): Promise<DownloadedFile>;
}

export interface ExportButtonProps {
	type: string;
	api: ExportApi;
	/** The screen's current filters, passed through unchanged. */
	filters?: URLSearchParams;
	t: Translate;
	/** Told when an export fails, so the screen can raise its own toast. */
	onError?: (error: Error) => void;
}

export function ExportButton({ type, api, filters, t, onError }: ExportButtonProps) {
	const [busy, setBusy] = useState(false);

	async function run(format: "csv" | "xlsx") {
		setBusy(true);
		try {
			saveFile(await api.downloadExport(type, filters, format));
		} catch (cause) {
			onError?.(cause instanceof Error ? cause : new Error(String(cause)));
		} finally {
			setBusy(false);
		}
	}

	return (
		<DropdownMenu
			label={t("patterns.export.label")}
			trigger={
				<Button variant="secondary" icon={Download} loading={busy}>
					{t("patterns.export.label")}
				</Button>
			}
			groups={[
				[
					{ id: "csv", label: t("patterns.export.csv"), onSelect: () => void run("csv") },
					{ id: "xlsx", label: t("patterns.export.xlsx"), onSelect: () => void run("xlsx") },
				],
			]}
		/>
	);
}
